import {
  PropertiesContext,
  PropertiesContextType,
} from '@/contexts/PropertiesContext'
import { stPropertyRepository } from '@/repositories/StPropertyRepository'
import {
  StaysPropertyFormInput,
  StaysPropertyFormOutput,
  staysPropertySchema,
} from '@/schemas/stays/staysPropertySchema'
import type { StProperty, StPropertyUpdate } from '@/types'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useContext, useMemo } from 'react'
import {
  Control,
  FieldErrors,
  useForm,
  UseFormHandleSubmit,
  UseFormReturn,
} from 'react-hook-form'
import { getDefaultValues } from '../../utils/staysPropertyFormDefaults'
import { StPropertyInsertWithEndpoint } from '../staysApi/useCreateStaysProperties'
import { useCRUD } from '../useCRUD'

interface UseStaysPropertyFormOptions {
  st_property?: StProperty | null
}

interface useStaysPropertyFormReturn {
  // Form
  form: UseFormReturn<StaysPropertyFormInput, any, StaysPropertyFormOutput>
  formValues: StaysPropertyFormInput
  control: Control<StaysPropertyFormInput, any, StaysPropertyFormOutput>
  errors: FieldErrors<StaysPropertyFormOutput>

  // State
  isLoading: boolean
  isSubmitting: boolean
  isOpen: boolean
  selectedStaysProperty: StProperty | null

  // Actions
  setSelectedStaysProperty: React.Dispatch<
    React.SetStateAction<StProperty | null>
  >
  onSubmit: (data: StaysPropertyFormOutput) => Promise<void>
  onOpen: () => void
  onClose: () => void
  handleSubmit: UseFormHandleSubmit<
    StaysPropertyFormInput,
    StaysPropertyFormOutput
  >
}

/**
 * Main hook for property form with React Hook Form
 * Orchestrates form state, validation, step navigation, and submission
 * Optimized for performance and minimal re-renders
 */
export function useStaysPropertyForm({
  st_property,
}: UseStaysPropertyFormOptions): useStaysPropertyFormReturn {
  const {
    selectedStaysProperty,
    isStaysPropertiesOpen,
    onStaysPropertiesClose,
    setSelectedStaysProperty,
    onStaysPropertiesOpen,
  } = useContext<PropertiesContextType | undefined>(
    PropertiesContext
  ) as PropertiesContextType
  // Memoized default values based on property
  const defaultValues = useMemo<StaysPropertyFormOutput>(
    () => getDefaultValues(st_property),
    [st_property]
  )

  // Form setup with memoized default values and explicit type handling
  const form = useForm<StaysPropertyFormInput, any, StaysPropertyFormOutput>({
    resolver: zodResolver<StaysPropertyFormInput, any, StaysPropertyFormOutput>(
      staysPropertySchema
    ),
    mode: 'onBlur', // Changed from "onChange" to prevent aggressive validation triggering submission
    defaultValues,
  })

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = form

  const formValues = watch()
  console.log(formValues)

  const createStPropertyOnSuccess = (
    operation: 'create' | 'update' | 'delete' | 'restore',
    data?: StProperty | null | void
  ) => {
    if (operation === 'create') {
      if (data !== undefined) {
        if (Array.isArray(data)) {
          setSelectedStaysProperty(data[0])
        } else {
          setSelectedStaysProperty(data)
        }
        onStaysPropertiesOpen()
      }
    }
  }
  const { createAsync, updateAsync, isCreating, isUpdating } = useCRUD<
    StProperty,
    StPropertyInsertWithEndpoint,
    StPropertyUpdate
  >({
    repository: stPropertyRepository,
    queryKey: ['st_properties'],
    onSuccess: createStPropertyOnSuccess,
  })

  // Form submission handler
  const onSubmit = useCallback(
    async (data: StaysPropertyFormOutput) => {
      try {
        if (st_property?.id) {
          const updatedStProperty = await updateAsync({
            id: st_property.id,
            data,
          })
          setSelectedStaysProperty(updatedStProperty)
        } else {
          await createAsync({
            data,
            endpoint: 'stays-api-create-property',
          })
        }
      } catch (error) {
        console.error('Submission failed:', error)
        // Errors are handled by useCRUD's toast notifications
      }
    },
    [st_property, createAsync, updateAsync, setSelectedStaysProperty]
  )

  return {
    // Form
    form,
    formValues,
    control,
    errors,
    handleSubmit,
    onSubmit,
    // State
    isLoading: isCreating || isUpdating,
    isSubmitting: isCreating || isUpdating,
    isOpen: isStaysPropertiesOpen,
    onOpen: onStaysPropertiesOpen,
    onClose: onStaysPropertiesClose,
    selectedStaysProperty,
    setSelectedStaysProperty,
  }
}
